import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Play, ExternalLink, Mail } from 'lucide-react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import { getClient } from '../lib/api';
import Seo from '../components/Seo';

export default function ClientProfile() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    getClient(slug).then(setC).catch(() => setErr(true));
  }, [slug]);

  if (err) return (
    <div className="pt-48 pb-24 max-w-[1500px] mx-auto px-6 lg:px-10">
      <h1 className="font-heading text-5xl uppercase text-[#002b5c]">Actor not found.</h1>
      <Link to="/clients" className="mt-6 inline-block text-[11px] tracking-[0.22em] uppercase text-[#C5A059] gold-underline">Back to Clients</Link>
    </div>
  );
  if (!c) return <div className="pt-48 pb-24 text-center text-[#6b6b6b]">Loading…</div>;

  const stat = (label, value) => (
    <div>
      <p className="eyebrow">{label}</p>
      <p className="mt-2 font-heading text-lg md:text-xl text-[#002b5c] uppercase leading-tight">{value}</p>
    </div>
  );

  return (
    <div data-testid="client-profile-page" className="bg-white">
      <Seo
        title={c.name}
        description={
          (c.bio && c.bio.length > 30
            ? c.bio
            : `${c.name} is represented by David Ball Management for work across television, film, theatre, commercials and voice.`
          ).slice(0, 300)
        }
        path={`/clients/${c.slug}`}
        image={c.headshot && c.headshot.startsWith('http') ? c.headshot : undefined}
        type="profile"
      />
      {/* Header */}
      <section className="pt-32 pb-8 lg:pt-40 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Link to="/clients" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] gold-underline">
            <ArrowLeft size={14} /> All Clients
          </Link>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-8 pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-6">
            <div className="img-hover rounded-2xl overflow-hidden aspect-[4/5] bg-neutral-100">
              <img src={c.headshot} alt={c.name} className="w-full h-full object-cover" />
            </div>
          </Reveal>

          <div className="lg:col-span-6 lg:pl-8">
            <Reveal delay={120}>
              <p className="eyebrow">Represented by David Ball Management</p>
              <h1 className="font-heading uppercase text-[#002b5c] text-5xl md:text-6xl lg:text-7xl mt-4 leading-[0.9]">
                {c.name}
              </h1>
              <p className="mt-8 text-lg text-[#4a4a4a] leading-relaxed">{c.bio}</p>
            </Reveal>

            <Reveal delay={200}>
              <div className="hairline my-10" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6">
                {stat('Playing Age', c.playing_age || '·')}
                {stat('Height', c.height || '·')}
                {stat('Location', c.location || '·')}
                {stat('Hair', c.hair_colour || '·')}
                {stat('Eyes', c.eye_colour || '·')}
              </div>
            </Reveal>

            <Reveal delay={280}>
              <div className="mt-10 flex flex-wrap gap-3">
                {c.showreel_url && (
                  <a
                    href={c.showreel_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="client-cta-reel"
                    className="inline-flex items-center gap-2 rounded-full bg-[#002b5c] text-white px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#001a38] transition-colors"
                  >
                    <Play size={14} /> Watch Showreel
                  </a>
                )}
                {c.spotlight_url && (
                  <a
                    href={c.spotlight_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="client-cta-spotlight"
                    className="inline-flex items-center gap-2 rounded-full border border-[#002b5c]/30 text-[#002b5c] px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#002b5c] hover:text-white transition-colors"
                  >
                    <ExternalLink size={14} /> Spotlight Profile
                  </a>
                )}
                {c.cv_url && (
                  <a
                    href={c.cv_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="client-cta-cv"
                    className="inline-flex items-center gap-2 rounded-full border border-[#002b5c]/30 text-[#002b5c] px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#002b5c] hover:text-white transition-colors"
                  >
                    <Download size={14} /> Download CV
                  </a>
                )}
                <Link to="/contact" data-testid="client-cta-enquire" className="inline-flex items-center gap-2 rounded-full bg-[#C5A059] text-[#001a38] px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#002b5c] hover:text-white transition-colors">
                  <Mail size={14} /> Enquire Availability
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Skills & Accents */}
      {(c.accents.length > 0 || c.skills.length > 0 || c.training.length > 0) && (
      <section className="bg-[#faf8f4] py-24 lg:py-32">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          {c.accents.length > 0 && (
          <Reveal className="lg:col-span-4">
            <SectionEyebrow>Accents</SectionEyebrow>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.accents.map(a => (
                <span key={a} className="rounded-full bg-white border border-black/10 px-4 py-2 text-xs tracking-[0.15em] uppercase text-[#002b5c]">{a}</span>
              ))}
            </div>
          </Reveal>
          )}
          {c.skills.length > 0 && (
          <Reveal className="lg:col-span-4" delay={100}>
            <SectionEyebrow>Skills</SectionEyebrow>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.skills.map(s => (
                <span key={s} className="rounded-full bg-white border border-black/10 px-4 py-2 text-xs tracking-[0.15em] uppercase text-[#002b5c]">{s}</span>
              ))}
            </div>
          </Reveal>
          )}
          {c.training.length > 0 && (
          <Reveal className="lg:col-span-4" delay={200}>
            <SectionEyebrow>Training</SectionEyebrow>
            <ul className="mt-6 space-y-3 text-sm text-[#333]">
              {c.training.map(t => <li key={t} className="flex gap-3"><span className="w-1 h-1 bg-[#C5A059] rounded-full mt-2.5" />{t}</li>)}
            </ul>
          </Reveal>
          )}
        </div>
      </section>
      )}

      {/* Credits */}
      {c.credits.length > 0 && (
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <Reveal>
          <SectionEyebrow>Selected Credits</SectionEyebrow>
          <h2 className="font-heading text-4xl md:text-5xl uppercase mt-6 text-[#002b5c]">The work.</h2>
        </Reveal>
        <div className="mt-12 divide-y divide-black/10 border-t border-black/10">
          {c.credits.map((cr, i) => (
            <Reveal key={`${cr.medium}-${cr.production}-${i}`} delay={i * 60}>
              <div className="grid grid-cols-12 gap-4 py-6 items-center">
                <p className="col-span-3 md:col-span-2 text-[11px] tracking-[0.22em] uppercase text-[#C5A059]">{cr.medium}</p>
                <p className="col-span-9 md:col-span-4 font-heading text-xl md:text-2xl uppercase text-[#002b5c] leading-tight">{cr.production}</p>
                <p className="col-span-6 md:col-span-3 text-sm text-[#4a4a4a]">{cr.role}</p>
                <p className="col-span-6 md:col-span-3 text-sm text-[#6b6b6b] md:text-right">{cr.company}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}
